import React, { useState } from "react";
import {
  StyledSelect,
  StyledTextField,
} from "../../../customComponents/styled/styledInputs";
import { useFormik } from "formik";
import * as yup from "yup";
import {
  Box,
  FormControl,
  FormHelperText,
  Grid,
  MenuItem,
  Typography,
  Autocomplete,
} from "@mui/material";
import { StyledLoadingButton } from "../../../customComponents/styled/styledButtons";
import { useCreateGradeMutation } from "./gradesApiSlice";
import { useGetSubSectionsPaginatedQuery } from "../sections/sectionsApiSlice";
import toast from "../../../../helpers/toast";

const validationSchema = yup.object({
  name: yup.string().required("Grade is required"),
  min_score: yup
    .number("must be a number")
    .max(100, "max is 100")
    .min(0, "min is 0")
    .when("type", {
      is: "comment",
      then: (s) => s.notRequired(),
      otherwise: (s) => s.required("Required"),
    }),
  max_score: yup
    .number()
    .max(100, "max is 100")
    .min(0, "min is 0")
    .when("type", {
      is: "comment",
      then: (s) => s.notRequired(),
      otherwise: (s) => s.required("Required"),
    }),
  type: yup.string().required("Required"),
  description: yup.string().required("Required"),
  subsection_id: yup.string().required("Required"),
});

function CreateGradeForm() {
  const [subsectionSearch, setSubsectionSearch] = useState("");
  const { data: subsectionsData } = useGetSubSectionsPaginatedQuery({ page: 1, per_page: 10, q: subsectionSearch });
  const subsections = subsectionsData?.subsections ?? [];
  const [createGrade, { isLoading: createGradeLoading }] =
    useCreateGradeMutation();

  const formik = useFormik({
    initialValues: {
      name: "",
      description: "",
      type: "",
      min_score: "",
      max_score: "",
      subsection_id: "",
    },
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      // handleCreateGrade(values)
      let items =
        values.subsection_id === "all"
          ? subsections?.map((subsection) => ({
              ...values,
              subsection_id: subsection.id,
            }))
          : [{ ...values }];

      handleCreateGrade(items);
    },
  });

  const handleCreateGrade = async (items) => {
    items.forEach(async (item) => {
      const payload = {
        ...item,
        min_score: item.min_score !== "" ? item.min_score : null,
        max_score: item.max_score !== "" ? item.max_score : null,
      };
      // eslint-disable-next-line no-param-reassign
      item = payload;
      try {
        const response = await createGrade(item).unwrap();
        if (response.id) {
          toast.success("Grade created successfully");
          formik.setValues((prev) => ({
            ...prev,
            name: "",
            description: "",
            min_score: "",
            max_score: "",
          }));
          formik.setTouched({});
        } else {
          toast.error("Error creating grade");
        }
      } catch (error) {
        toast.error(error?.message || "Something went wrong");
      }
    });
  };

  return (
    <>
      <Box className="paperHeader">
        <Typography variant="formHeading">Setup Grades</Typography>
      </Box>
      <Box className="paperBody">
        <form onSubmit={formik.handleSubmit}>
          <Grid container columnSpacing={1} rowSpacing={2}>
            <Grid item xs={12}>
              <FormControl error fullWidth>
                <Typography variant="formSubHeading">Grade</Typography>
                <StyledTextField
                  variant="outlined"
                  id="name"
                  name="name"
                  value={formik.values.name}
                  onChange={formik.handleChange}
                  error={formik.touched.name && Boolean(formik.errors.name)}
                />
                <FormHelperText variant="error">
                  {formik.touched.name && formik.errors.name}
                </FormHelperText>
              </FormControl>
            </Grid>

            {/* {second Row begin} */}

            <Grid item xs={12} lg={6}>
              <FormControl error fullWidth>
                <Typography variant="formSubHeading">Minimum score</Typography>
                <StyledTextField
                  variant="outlined"
                  id="min_score"
                  name="min_score"
                  value={formik.values.min_score}
                  onChange={formik.handleChange}
                  error={
                    formik.touched.min_score && Boolean(formik.errors.min_score)
                  }
                />
                <FormHelperText variant="error">
                  {formik.touched.min_score && formik.errors.min_score}
                </FormHelperText>
              </FormControl>
            </Grid>
            <Grid item xs={12} lg={6}>
              <FormControl error fullWidth>
                <Typography variant="formSubHeading">Maximum Score</Typography>
                <StyledTextField
                  variant="outlined"
                  id="max_score"
                  name="max_score"
                  value={formik.values.max_score}
                  onChange={formik.handleChange}
                  error={
                    formik.touched.max_score && Boolean(formik.errors.max_score)
                  }
                />
                <FormHelperText variant="error">
                  {formik.touched.max_score && formik.errors.max_score}
                </FormHelperText>
              </FormControl>
            </Grid>

            {/* end of second row  */}

            <Grid item xs={12}>
              <FormControl error fullWidth>
                <Typography variant="formSubHeading">Description</Typography>
                <StyledTextField
                  variant="outlined"
                  id="description"
                  name="description"
                  value={formik.values.description}
                  onChange={formik.handleChange}
                  error={
                    formik.touched.description &&
                    Boolean(formik.errors.description)
                  }
                />
                <FormHelperText variant="error">
                  {formik.touched.description && formik.errors.description}
                </FormHelperText>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl
                error={formik.touched.type && formik.errors.type}
                fullWidth
              >
                <Typography variant="formSubHeading">type</Typography>
                <StyledSelect
                  displayEmpty
                  variant="outlined"
                  id="type"
                  name="type"
                  value={formik.values.type}
                  onChange={formik.handleChange}
                  error={formik.touched.type && Boolean(formik.errors.type)}
                >
                  <MenuItem value="" disabled>
                    <em>select type</em>
                  </MenuItem>
                  <MenuItem value="score">score</MenuItem>
                  <MenuItem value="comment">comment</MenuItem>
                </StyledSelect>
                <FormHelperText variant="error">
                  {formik.touched.type && formik.errors.type}
                </FormHelperText>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <FormControl
                error={
                  formik.touched.subsection_id && formik.errors.subsection_id
                }
                fullWidth
              >
                <Typography variant="formSubHeading">sub-section</Typography>
                <Autocomplete
                  options={[{ id: "all", name: "All subsections" }, ...subsections]}
                  getOptionLabel={(option) => option.name || ""}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  value={[{ id: "all", name: "All subsections" }, ...subsections].find(s => s.id === formik.values.subsection_id) || null}
                  onChange={(_, value) => formik.setFieldValue("subsection_id", value?.id || "")}
                  onInputChange={(_, value, reason) => { if (reason === "input") setSubsectionSearch(value); }}
                  filterOptions={(x) => x}
                  renderInput={(params) => (
                    <StyledTextField
                      {...params}
                      placeholder="Search subsection..."
                      error={formik.touched.subsection_id && Boolean(formik.errors.subsection_id)}
                    />
                  )}
                />
                <FormHelperText variant="error">
                  {formik.touched.subsection_id && formik.errors.subsection_id}
                </FormHelperText>
              </FormControl>
            </Grid>

            <Grid item xs={12} textAlign={"right"}>
              <StyledLoadingButton loading={createGradeLoading} type="submit">
                Submit
              </StyledLoadingButton>
            </Grid>
          </Grid>
        </form>
      </Box>
    </>
  );
}

export default CreateGradeForm;
